import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import PaymentsIcon from "@mui/icons-material/Payments";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { API_URL } from "./config";

const ORANGE = "#F07828";
const ORANGE_2 = "#ed7f35";
const BLUE = "rgba(25,118,210,0.95)";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";

const cardSx = {
  borderRadius: 2,
  border: `1px solid ${BORDER}`,
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
  bgcolor: "#fff",
};

const sectionSx = {
  ...cardSx,
  p: 2.5,
  position: "relative",
  breakInside: "avoid",

  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    background: `linear-gradient(
      180deg,
      ${ORANGE} 0%,
      ${ORANGE_2} 100%
    )`,
  },
};

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(25,118,210,0.45)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: ORANGE,
  },
};

const headCellSx = {
  fontWeight: 900,
  bgcolor: BLUE,
  color: "#fff",
  whiteSpace: "nowrap",
  py: 1,
};

const cellSx = {
  whiteSpace: "nowrap",
  fontSize: 12,
  py: 0.8,
};

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function displayValue(value) {
  return value === null ||
    value === undefined ||
    value === ""
    ? "-"
    : value;
}

function pct(value) {
  if (value === null || value === undefined) {
    return "-";
  }

  return `${Number(value).toFixed(2)}%`;
}

function dateOnly(value) {
  if (!value) return "-";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function uniqueStrings(values) {
  return [
    ...new Set(
      (values || [])
        .map((item) => {
          if (item === null || item === undefined) {
            return null;
          }

          if (typeof item === "object") {
            return (
              item.v ??
              item.value ??
              item.code ??
              item.label ??
              null
            );
          }

          return String(item);
        })
        .filter(Boolean)
        .map(String)
    ),
  ];
}

function getStudentKey(student) {
  return String(
    student.StudentKey || student.StudentID
  );
}

function normalizeExternalUrl(url) {
  if (!url) return null;

  const trimmed = String(url).trim();

  if (!trimmed) return null;

  // Already a complete external URL.
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Prevent the browser from turning it into:
  // http://localhost:3000/example.com
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function waitForRender(delay = 100) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, delay);
      });
    });
  });
}

function parseStudentIds(value) {
  return [
    ...new Set(
      String(value || "")
        .split(/[\s,;\n\r]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ];
}

function StatusChip({ status }) {
  const normalized = String(
    status || "-"
  ).toUpperCase();

  let color = "default";

  if (
    ["PAID", "APPROVED", "EVALUATED"].includes(
      normalized
    )
  ) {
    color = "success";
  } else if (
    ["PARTIAL", "SUBMITTED"].includes(normalized)
  ) {
    color = "warning";
  } else if (
    ["DUE", "REJECTED"].includes(normalized)
  ) {
    color = "error";
  }

  return (
    <Chip
      size="small"
      label={normalized}
      color={color}
      variant="outlined"
    />
  );
}

function StatCard({
  icon,
  title,
  value,
  subtitle,
}) {
  return (
    <Paper sx={{ ...cardSx, p: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: "rgba(25,118,210,0.08)",
            color: "#1d4ed8",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: "#0f172a",
            }}
          >
            {value}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionTitle({
  title,
  subtitle,
}) {
  return (
    <Box sx={{ mb: 2, pl: 1 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 900,
          color: ORANGE,
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function ReportContent({ report }) {
  const marksBySemester = useMemo(() => {
    return (report?.marks || []).reduce(
      (accumulator, row) => {
        const semester = row.Semester || "NA";

        if (!accumulator[semester]) {
          accumulator[semester] = [];
        }

        accumulator[semester].push(row);

        return accumulator;
      },
      {}
    );
  }, [report]);

  if (!report) return null;

  return (
    <Box
      className="student-progress-report"
      sx={{
        width: 1120,
        bgcolor: "#fff",
        p: 2,
        color: "#111827",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <Paper
        sx={{
          ...cardSx,
          p: 3,
          mb: 2,
          background:
            "linear-gradient(135deg, #eff6ff 0%, #ffffff 55%, #fff7ed 100%)",
          borderTop: `5px solid ${ORANGE}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 950,
                color: "#172554",
              }}
            >
              {report.student.FullName}
            </Typography>

            <Typography
              sx={{
                color: "#475569",
                fontWeight: 800,
              }}
            >
              {report.student.StudentID}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              <Chip
                label={displayValue(
                  report.student.CampusCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.SubCampusCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.DegreeCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.BranchCode
                )}
                size="small"
              />

              <Chip
                label={`Sem ${displayValue(
                  report.student.Semester
                )}`}
                size="small"
              />

              <Chip
                label={`Sec ${displayValue(
                  report.student.Section
                )}`}
                size="small"
              />
            </Stack>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Academic Year
            </Typography>

            <Typography
              variant="h6"
              sx={{ fontWeight: 900 }}
            >
              {displayValue(
                report.student.AcademicYear
              )}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Regulation:{" "}
              {displayValue(
                report.student.Regulation
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Grid
        container
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={3}>
          <StatCard
            icon={<TrendingUpIcon />}
            title="Attendance"
            value={pct(
              report.summary?.attendance
                ?.AttendancePercentage
            )}
            subtitle={`Rank ${displayValue(
              report.summary?.attendance
                ?.AttendanceRank
            )} / ${displayValue(
              report.summary?.attendance
                ?.TotalPeers
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<SchoolIcon />}
            title="CGPA"
            value={displayValue(
              report.summary?.cgpa
            )}
            subtitle={`Backlogs: ${displayValue(
              report.summary?.backlogCount
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<PaymentsIcon />}
            title="Fee Due"
            value={money(
              report.summary?.totalFeeDue
            )}
            subtitle={`Paid: ${money(
              report.summary?.totalFeePaid
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<EventBusyIcon />}
            title="Leave Days"
            value={displayValue(
              report.summary?.totalLeaveDays
            )}
            subtitle={`Projects: ${displayValue(
              report.summary?.projectCount
            )}`}
          />
        </Grid>
      </Grid>

      <Paper sx={sectionSx}>
        <SectionTitle title="Attendance Summary" />

        <Grid
          container
          spacing={1.5}
          sx={{ pl: 1 }}
        >
          {[
            [
              "Total Sessions",
              report.attendance?.TotalSessions,
            ],
            [
              "Present",
              report.attendance?.PresentSessions,
            ],
            [
              "Absent",
              report.attendance?.AbsentSessions,
            ],
            [
              "Percentage",
              pct(
                report.attendance
                  ?.AttendancePercentage
              ),
            ],
            [
              "Class Average",
              pct(
                report.attendance
                  ?.ClassAverageAttendance
              ),
            ],
            [
              "Highest",
              pct(
                report.attendance
                  ?.HighestAttendance
              ),
            ],
            [
              "Lowest",
              pct(
                report.attendance
                  ?.LowestAttendance
              ),
            ],
            [
              "Rank",
              `${displayValue(
                report.attendance?.AttendanceRank
              )} / ${displayValue(
                report.attendance?.TotalPeers
              )}`,
            ],
          ].map(([label, value]) => (
            <Grid item xs={3} key={label}>
              <Box
                sx={{
                  p: 1.3,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {label}
                </Typography>

                <Typography
                  sx={{ fontWeight: 900 }}
                >
                  {displayValue(value)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Marks & Academic Performance" />

        {Object.keys(marksBySemester).length ===
        0 ? (
          <Alert severity="info">
            No marks found.
          </Alert>
        ) : (
          Object.entries(
            marksBySemester
          ).map(([semester, rows]) => (
            <Box
              key={semester}
              sx={{
                mb: 2.5,
                pl: 1,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#1e3a8a",
                  }}
                >
                  Semester {semester}
                </Typography>

                <Chip
                  size="small"
                  label={`GPA ${displayValue(
                    rows[0]?.SemesterGPA
                  )} | Rank ${displayValue(
                    rows[0]?.SemesterGPARank
                  )} / ${displayValue(
                    rows[0]?.TotalPeersInSemester
                  )}`}
                />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>
                      Subject
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Grade
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Point
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Subject Rank
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Class Avg
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Highest
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Lowest
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((mark, index) => (
                    <TableRow
                      key={`${mark.SubjectCode}-${index}`}
                    >
                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectName
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(mark.Grade)}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.GradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectRank
                        )}{" "}
                        /{" "}
                        {displayValue(
                          mark.TotalPeersInSubject
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectClassAverageGradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectHighestGradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectLowestGradePoint
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        )}
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Fee Details" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Sem
              </TableCell>

              <TableCell sx={headCellSx}>
                Fee
              </TableCell>

              <TableCell sx={headCellSx}>
                Amount
              </TableCell>

              <TableCell sx={headCellSx}>
                Paid
              </TableCell>

              <TableCell sx={headCellSx}>
                Due
              </TableCell>

              <TableCell sx={headCellSx}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.fees || []).map(
              (fee, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      fee.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(fee.Semester)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(fee.FeeLabel)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeeAmount)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeePaid)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeeDue)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <StatusChip
                      status={fee.PaymentStatus}
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Academic Projects" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Subjects
              </TableCell>

              <TableCell sx={headCellSx}>
                Project
              </TableCell>

              <TableCell sx={headCellSx}>
                Marks
              </TableCell>

              <TableCell sx={headCellSx}>
                Links
              </TableCell>

              <TableCell sx={headCellSx}>
                Remarks
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.projects || []).map(
              (project, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.Subjects
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.ProjectTitle
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(project.Marks)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <Stack
                      direction="row"
                      spacing={0.6}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {project.GitHubUrl && (
                        <Chip
                          size="small"
                          label="GitHub"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.GitHubUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.GitHubUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#f8fafc",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.YouTubeUrl && (
                        <Chip
                          size="small"
                          label="Video"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.YouTubeUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.YouTubeUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#fef2f2",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.NotionPromptUrl && (
                        <Chip
                          size="small"
                          label="Notion"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.NotionPromptUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.NotionPromptUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#f8fafc",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.DeploymentUrl && (
                        <Chip
                          size="small"
                          label="Live"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.DeploymentUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.DeploymentUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#ecfdf5",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {!project.GitHubUrl &&
                        !project.YouTubeUrl &&
                        !project.NotionPromptUrl &&
                        !project.DeploymentUrl && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            -
                          </Typography>
                        )}
                    </Stack>
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellSx,
                      whiteSpace: "normal",
                    }}
                  >
                    {displayValue(
                      project.TeacherRemarks
                    )}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Time Off Requests" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Leave Type
              </TableCell>

              <TableCell sx={headCellSx}>
                Start
              </TableCell>

              <TableCell sx={headCellSx}>
                End
              </TableCell>

              <TableCell sx={headCellSx}>
                Days
              </TableCell>

              <TableCell sx={headCellSx}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.timeOff || []).map(
              (timeOff, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.LeaveType
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {dateOnly(
                      timeOff.LeaveStartDate
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {dateOnly(
                      timeOff.LeaveEndDate
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.Duration
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <StatusChip
                      status={timeOff.Status}
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Events & Assignments" />

        <Stack
          direction="row"
          spacing={1}
          sx={{ pl: 1 }}
        >
          <Chip
            icon={<AssignmentTurnedInIcon />}
            label={`Assignments: ${
              (report.assignments || []).length
            }`}
          />

          <Chip
            label={`Events: ${
              (report.events || []).length
            }`}
          />
        </Stack>
      </Paper>

      <Divider sx={{ my: 2.5 }} />

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        Generated by UniFLOO Student Progress Report
      </Typography>
    </Box>
  );
}

export default function StudentDetailReport() {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const pdfStageRef = useRef(null);

  const [studentIdSearch,setStudentIdSearch,] = useState("");

  const previewQuery = useMemo(() => {
  const searchParams =
      new URLSearchParams(
        window.location.search
      );
  
    return {
      StudentKey:
        searchParams.get(
          "previewStudentKey"
        ),
      
      StudentID:
        searchParams.get(
          "previewStudentId"
        ),
    };
  }, []);
  
  const isPreviewWindow = Boolean(
    previewQuery.StudentKey ||
    previewQuery.StudentID
  );

  const token =
    localStorage.getItem("auth") ||
    localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const campusCode = useMemo(() => {
    if (!token) return "";

    try {
      const decoded = JSON.parse(
        atob(token.split(".")[1])
      );

      return (
        decoded.campusCode ||
        decoded.CampusCode ||
        ""
      );
    } catch (error) {
      console.error(
        "Failed to decode token:",
        error
      );

      return "";
    }
  }, [token]);

  const [filters, setFilters] = useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Section: [],
  });

  const [options, setOptions] = useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Section: [],
  });

  const [students, setStudents] = useState([]);

  const [
    selectedStudentIds,
    setSelectedStudentIds,
  ] = useState([]);

  const [
    previewReport,
    setPreviewReport,
  ] = useState(null);

  const [pdfReports, setPdfReports] =
    useState([]);

  const [
    loadingLookups,
    setLoadingLookups,
  ] = useState(false);

  const [
    loadingStudents,
    setLoadingStudents,
  ] = useState(false);

  const [
    loadingReport,
    setLoadingReport,
  ] = useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [error, setError] = useState("");

  const loadLookups = async ({
    subCampusCode = "",
    degreeCode = "",
  } = {}) => {
    if (!campusCode) return;

    try {
      setLoadingLookups(true);

      const response = await axios.get(
        `${API_URL}/api/cascadelookups/all`,
        {
          headers: authHeaders,

          params: {
            campusCode,

            subCampusCode:
              subCampusCode || undefined,

            degreeCode:
              degreeCode || undefined,
          },
        }
      );

      const data = response.data;

      setOptions((previous) => ({
        ...previous,

        AcademicYear: uniqueStrings(
          data?.AcademicYear
        ),

        SubCampusCode: uniqueStrings(
          data?.SubCampusCode
        ),

        DegreeCode: uniqueStrings(
          data?.DegreeCode
        ),

        BranchCode: uniqueStrings(
          data?.BranchCode
        ),

        SemesterCode: uniqueStrings(
          data?.SemesterCode
        ),

        Section: uniqueStrings(
          data?.Section
        ),
      }));
    } catch (error) {
      console.error(error);

      setError(
        error.response?.data?.error ||
          "Failed to load filter options"
      );
    } finally {
      setLoadingLookups(false);
    }
  };

  useEffect(() => {
    if (isPreviewWindow) return;
    loadLookups();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode, isPreviewWindow]);

  const handleSubCampusChange = async (
    event,
    values
  ) => {
    setFilters((previous) => ({
      ...previous,

      SubCampusCode: values,
      DegreeCode: [],
      BranchCode: [],
    }));

    setOptions((previous) => ({
      ...previous,
      DegreeCode: [],
      BranchCode: [],
    }));

    if (values.length === 1) {
      await loadLookups({
        subCampusCode: values[0],
      });
    } else {
      await loadLookups();
    }
  };

  const handleDegreeChange = async (
    event,
    values
  ) => {
    setFilters((previous) => ({
      ...previous,
      DegreeCode: values,
      BranchCode: [],
    }));

    setOptions((previous) => ({
      ...previous,
      BranchCode: [],
    }));

    if (
      filters.SubCampusCode.length === 1 &&
      values.length === 1
    ) {
      await loadLookups({
        subCampusCode:
          filters.SubCampusCode[0],

        degreeCode: values[0],
      });
    } else if (
      filters.SubCampusCode.length === 1
    ) {
      await loadLookups({
        subCampusCode:
          filters.SubCampusCode[0],
      });
    }
  };

  const resetFilters = () => {
    setFilters({
      AcademicYear: [],
      SubCampusCode: [],
      DegreeCode: [],
      BranchCode: [],
      SemesterCode: [],
      Section: [],
    });

    setStudentIdSearch("");
    setStudents([]);
    setSelectedStudentIds([]);
    setPreviewReport(null);
    setError("");

    loadLookups();
  };

  const buildFilterParams = () => {
    const params = {
      CampusCode: campusCode,
    };
  
    Object.entries(filters).forEach(
      ([key, values]) => {
        if (values.length) {
          params[key] =
            values.join(",");
        }
      }
    );
  
    const studentIds =
      parseStudentIds(
        studentIdSearch
      );
  
    if (studentIds.length) {
      params.StudentID =
        studentIds.join(",");
    }
  
    return params;
  };

  const loadStudents = async () => {
    const studentIds =
      parseStudentIds(
        studentIdSearch
      );
  
    const hasFilter =
      Object.values(filters).some(
        (values) =>
          Array.isArray(values) &&
          values.length > 0
      );
  
    if (
      !studentIds.length &&
      !hasFilter
    ) {
      setError(
        "Enter one or more Student IDs, or select at least one filter."
      );
  
      return;
    }
  
    try {
      setLoadingStudents(true);
      setError("");
      setStudents([]);
      setSelectedStudentIds([]);
      setPreviewReport(null);
  
      const response = await axios.get(
        `${API_URL}/api/studentDetailReport/reports/student-detail/students`,
        {
          headers: authHeaders,
          params:
            buildFilterParams(),
        }
      );
  
      const rows =
        Array.isArray(
          response.data
        )
          ? response.data
          : [];
  
      setStudents(rows);
  
      if (!rows.length) {
        setError(
          "No students found for the entered IDs or selected filters."
        );
      }
    } catch (error) {
      console.error(error);
  
      setError(
        error.response?.data?.error ||
          "Failed to load students"
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchReport = async (student) => {
    const params = {};
  
    if (student?.StudentKey) {
      params.StudentKey =
        student.StudentKey;
    } else if (student?.StudentID) {
      params.StudentID =
        student.StudentID;
    } else {
      throw new Error(
        "StudentKey or StudentID is required"
      );
    }
  
    const response = await axios.get(
      `${API_URL}/api/studentDetailReport/reports/student-detail`,
      {
        headers: authHeaders,
        params,
      }
    );
  
    return response.data;
  };

  useEffect(() => {
    if (!isPreviewWindow) return;
    if (!token) return;
  
    let cancelled = false;
  
    const loadPreviewReport = async () => {
      try {
        setLoadingReport(true);
        setError("");
  
        const report =
          await fetchReport({
            StudentKey:
              previewQuery.StudentKey,
  
            StudentID:
              previewQuery.StudentID,
          });
  
        if (!cancelled) {
          setPreviewReport(report);
        }
      } catch (error) {
        console.error(
          "Preview report error:",
          error
        );
  
        if (!cancelled) {
          setError(
            error.response?.data?.error ||
              "Failed to load student report"
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingReport(false);
        }
      }
    };
  
    loadPreviewReport();
  
    return () => {
      cancelled = true;
    };
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviewWindow,
    previewQuery.StudentKey,
    previewQuery.StudentID,
    token,
  ]);

  const previewStudent = (student) => {
    const previewUrl = new URL(
      window.location.href
    );
  
    // Remove old query values.
    previewUrl.search = "";
  
    if (student.StudentKey) {
      previewUrl.searchParams.set(
        "previewStudentKey",
        student.StudentKey
      );
    } else {
      previewUrl.searchParams.set(
        "previewStudentId",
        student.StudentID
      );
    }
  
    // Same application and route.
    // No localhost or domain is hard-coded.
    window.open(
      previewUrl.toString(),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const toggleStudent = (student) => {
    const key = getStudentKey(student);

    setSelectedStudentIds((previous) =>
      previous.includes(key)
        ? previous.filter(
            (studentId) =>
              studentId !== key
          )
        : [...previous, key]
    );
  };

  const allSelected =
    students.length > 0 &&
    selectedStudentIds.length ===
      students.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedStudentIds([]);
      return;
    }

    setSelectedStudentIds(
      students.map((student) =>
        getStudentKey(student)
      )
    );
  };

  const selectedStudents = useMemo(
    () =>
      students.filter((student) =>
        selectedStudentIds.includes(
          getStudentKey(student)
        )
      ),
    [students, selectedStudentIds]
  );

  const addCanvasToPdf = (
    pdf,
    sourceCanvas,
    reportNode
  ) => {
    const pageWidthMm =
      pdf.internal.pageSize.getWidth();
  
    const pageHeightMm =
      pdf.internal.pageSize.getHeight();
  
    const pixelsPerMm =
      sourceCanvas.width / pageWidthMm;
  
    const pageHeightPx = Math.floor(
      pageHeightMm * pixelsPerMm
    );
  
    /*
     * Find all visible project links before
     * splitting the report into PDF pages.
     */
    const reportRect =
      reportNode.getBoundingClientRect();
  
    const canvasScaleX =
      sourceCanvas.width /
      reportRect.width;
  
    const canvasScaleY =
      sourceCanvas.height /
      reportRect.height;
  
    const pdfLinks = Array.from(
      reportNode.querySelectorAll(
        "[data-pdf-url]"
      )
    )
      .map((element) => {
        const url =
          element.getAttribute(
            "data-pdf-url"
          );
  
        const rect =
          element.getBoundingClientRect();
  
        if (!url) return null;
  
        return {
          url,
  
          x:
            (rect.left -
              reportRect.left) *
            canvasScaleX,
  
          y:
            (rect.top -
              reportRect.top) *
            canvasScaleY,
  
          width:
            rect.width *
            canvasScaleX,
  
          height:
            rect.height *
            canvasScaleY,
        };
      })
      .filter(Boolean);
  
    let sourceY = 0;
    let pageIndex = 0;
  
    while (
      sourceY < sourceCanvas.height
    ) {
      const sliceHeight = Math.min(
        pageHeightPx,
        sourceCanvas.height -
          sourceY
      );
  
      const pageCanvas =
        document.createElement(
          "canvas"
        );
  
      pageCanvas.width =
        sourceCanvas.width;
  
      pageCanvas.height =
        sliceHeight;
  
      const context =
        pageCanvas.getContext(
          "2d",
          {
            alpha: false,
          }
        );
  
      context.fillStyle = "#ffffff";
  
      context.fillRect(
        0,
        0,
        pageCanvas.width,
        pageCanvas.height
      );
  
      context.drawImage(
        sourceCanvas,
        0,
        sourceY,
        sourceCanvas.width,
        sliceHeight,
        0,
        0,
        sourceCanvas.width,
        sliceHeight
      );
  
      if (pageIndex > 0) {
        pdf.addPage();
      }
  
      const imageData =
        pageCanvas.toDataURL(
          "image/jpeg",
          0.68
        );
  
      const renderedHeightMm =
        sliceHeight /
        pixelsPerMm;
  
      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidthMm,
        renderedHeightMm,
        undefined,
        "FAST"
      );
  
      /*
       * Add clickable link rectangles over
       * the project chips on this page.
       */
      pdfLinks.forEach((link) => {
        const linkTop =
          link.y;
  
        const linkBottom =
          link.y + link.height;
  
        const pageTop =
          sourceY;
  
        const pageBottom =
          sourceY + sliceHeight;
  
        const intersectsPage =
          linkBottom > pageTop &&
          linkTop < pageBottom;
  
        if (!intersectsPage) return;
  
        const clippedTop =
          Math.max(
            linkTop,
            pageTop
          );
  
        const clippedBottom =
          Math.min(
            linkBottom,
            pageBottom
          );
  
        const xMm =
          link.x /
          pixelsPerMm;
  
        const yMm =
          (clippedTop -
            pageTop) /
          pixelsPerMm;
  
        const widthMm =
          link.width /
          pixelsPerMm;
  
        const heightMm =
          (clippedBottom -
            clippedTop) /
          pixelsPerMm;
  
        pdf.link(
          xMm,
          yMm,
          widthMm,
          heightMm,
          {
            url: link.url,
          }
        );
      });
  
      sourceY += sliceHeight;
      pageIndex += 1;
    }
  };

  const downloadSelectedPdf =
    async () => {
      if (!selectedStudents.length) {
        setError(
          "Select at least one student."
        );
  
        return;
      }
  
      try {
        setPdfLoading(true);
        setError("");
  
        /*
         * One student is rendered and downloaded
         * at a time. This reduces browser memory
         * usage and creates a separate PDF for
         * every selected student.
         */
        for (
          let index = 0;
          index <
          selectedStudents.length;
          index += 1
        ) {
          const student =
            selectedStudents[index];
  
          const report =
            await fetchReport(student);
  
          /*
           * Render only this student's report
           * in the off-screen PDF area.
           */
          setPdfReports([report]);
  
          await waitForRender(150);
  
          const reportNode =
            pdfStageRef.current
              ?.querySelector(
                ".student-progress-report"
              );
  
          if (!reportNode) {
            throw new Error(
              `Could not render report for ${
                student.StudentID ||
                student.StudentKey
              }`
            );
          }
  
          const canvas =
            await html2canvas(
              reportNode,
              {
                scale: 1.15,
                useCORS: true,
                backgroundColor:
                  "#ffffff",
                logging: false,
                imageTimeout: 15000,
                removeContainer: true,
              }
            );
  
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
            compress: true,
            putOnlyUsedFonts: true,
          });
  
          addCanvasToPdf(
            pdf,
            canvas,
            reportNode
          );
  
          /*
           * html2canvas produces an image,
           * so links must be inserted into
           * the PDF separately.
           */
  
          const studentId =
            report?.student?.StudentID ||
            student.StudentID ||
            student.StudentKey ||
            `student_${index + 1}`;
  
          pdf.save(
            `Student_Progress_Report_${studentId}.pdf`
          );
  
          /*
           * Give the browser time to initiate
           * the previous file download.
           */
          await new Promise(
            (resolve) => {
              setTimeout(resolve, 350);
            }
          );
  
          setPdfReports([]);
        }
      } catch (error) {
        console.error(
          "PDF generation error:",
          error
        );
  
        setError(
          error.message ||
            "Failed to generate PDF files"
        );
      } finally {
        setPdfLoading(false);
        setPdfReports([]);
      }
    };

  const additionalFilterFields = [
    {
      key: "AcademicYear",
      label: "Academic Year",
      options: options.AcademicYear,
    },
    {
      key: "SemesterCode",
      label: "Semester",
      options: options.SemesterCode,
    },
    {
      key: "Section",
      label: "Section",
      options: options.Section,
    },
  ];

  if (isPreviewWindow) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: BG,
          p: {
            xs: 1,
            md: 2,
          },
        }}
      >
        {loadingReport && (
          <Paper
            sx={{
              ...cardSx,
              p: 4,
              textAlign: "center",
            }}
          >
            <CircularProgress />
  
            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 800,
              }}
            >
              Loading student report...
            </Typography>
          </Paper>
        )}
  
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}
  
        {previewReport &&
          !loadingReport && (
            <Box
              sx={{
                overflowX: "auto",
              }}
            >
              <ReportContent
                report={previewReport}
              />
            </Box>
          )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 2,
        },

        bgcolor: BG,
        minHeight: "100vh",
      }}
    >
      <Paper
        sx={{
          ...cardSx,
          mb: 2,
        }}
      >

<Box
  sx={{
    px: {
      xs: 2,
      md: 3,
    },

    py: {
      xs: 2,
      md: 2.5,
    },

    background: `linear-gradient(
      100deg,
      ${ORANGE} 0%,
      ${ORANGE_2} 55%,
      #f59e0b 100%
    )`,

    color: "#fff",
  }}
>
  <Stack
    direction={
      isMobile
        ? "column"
        : "row"
    }
    justifyContent="space-between"
    alignItems={
      isMobile
        ? "flex-start"
        : "center"
    }
    spacing={1.5}
  >
    <Box>
      <Typography
        sx={{
          fontSize: {
            xs: "1.25rem",
            md: "1.5rem",
          },

          fontWeight: 950,
          lineHeight: 1.2,
        }}
      >
        Student Progress Reports
      </Typography>

      <Typography
        sx={{
          opacity: 0.92,
          mt: 0.5,
          fontSize: "0.9rem",
        }}
      >
        Search students, preview individual
        reports, and download one PDF per
        selected student.
      </Typography>
    </Box>

    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
    >
      <Chip
        label={`${students.length} result(s)`}
        sx={{
          bgcolor:
            "rgba(255,255,255,0.18)",
          color: "#fff",
          fontWeight: 800,
        }}
      />

      <Chip
        label={`${selectedStudentIds.length} selected`}
        sx={{
          bgcolor:
            "rgba(255,255,255,0.18)",
          color: "#fff",
          fontWeight: 800,
        }}
      />
    </Stack>
  </Stack>
</Box>
{/*
        <Box
          sx={{
            px: 2,
            py: 1.5,

            background: `linear-gradient(
              90deg,
              ${ORANGE} 0%,
              ${ORANGE_2} 100%
            )`,

            color: "#fff",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.15rem",
              fontWeight: 900,
            }}
          >
            Student Progress Report
          </Typography>

          <Typography
            sx={{
              opacity: 0.9,
              fontSize: "0.85rem",
            }}
          >
            Filter students, select one or
            more records, preview and download
            reports.
          </Typography>
        </Box>
*/}
        <Box
          sx={{
            p: 2,
            bgcolor: BG,
          }}
        >
          <Stack spacing={2}>            
            {/* Cascade filters */}
            <Paper
              sx={{
                ...sectionSx,
                p: {
                  xs: 2,
                  md: 2.5,
                },
              }}
            >
              <Box sx={{ pl: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: ORANGE,
                      fontSize: "1.05rem",
                    }}
                  >
                    Filter Students
                  </Typography>
                
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Use these filters alone or together
                    with Student IDs.
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    display: "grid",
                  
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(6, minmax(0, 1fr))",
                    },
                
                    gap: 1.5,
                  }}
                >
                  <Autocomplete
                    multiple
                    options={
                      options.SubCampusCode
                    }
                    value={
                      filters.SubCampusCode
                    }
                    onChange={
                      handleSubCampusChange
                    }
                    loading={loadingLookups}
                    disableCloseOnSelect
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sub Campus"
                        size="small"
                        sx={inputSx}
                      />
                    )}
                  />
          
                  <Autocomplete
                    multiple
                    options={
                      options.DegreeCode
                    }
                    value={
                      filters.DegreeCode
                    }
                    onChange={
                      handleDegreeChange
                    }
                    loading={loadingLookups}
                    disabled={
                      filters.SubCampusCode
                        .length !== 1
                    }
                    disableCloseOnSelect
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Degree"
                        size="small"
                        sx={inputSx}
                        helperText={
                          filters.SubCampusCode
                            .length > 1
                            ? "Select one sub-campus for degree cascade"
                            : ""
                        }
                      />
                    )}
                  />
          
                  <Autocomplete
                    multiple
                    options={
                      options.BranchCode
                    }
                    value={
                      filters.BranchCode
                    }
                    onChange={(
                      event,
                      values
                    ) => {
                      setFilters(
                        (previous) => ({
                          ...previous,
                          BranchCode: values,
                        })
                      );
                    }}
                    loading={loadingLookups}
                    disabled={
                      filters.SubCampusCode
                        .length !== 1 ||
                      filters.DegreeCode
                        .length !== 1
                    }
                    disableCloseOnSelect
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Branch"
                        size="small"
                        sx={inputSx}
                      />
                    )}
                  />
          
                  {additionalFilterFields.map(
                    ({
                      key,
                      label,
                      options:
                        currentOptions,
                    }) => (
                      <Autocomplete
                        key={key}
                        multiple
                        options={
                          currentOptions
                        }
                        value={
                          filters[key]
                        }
                        onChange={(
                          event,
                          values
                        ) => {
                          setFilters(
                            (previous) => ({
                              ...previous,
                              [key]: values,
                            })
                          );
                        }}
                        disableCloseOnSelect
                        filterSelectedOptions
                        renderInput={(
                          params
                        ) => (
                          <TextField
                            {...params}
                            label={label}
                            size="small"
                            sx={inputSx}
                          />
                        )}
                      />
                    )
                  )}
                </Box>
              
                <Divider sx={{ my: 1 }} />
              
                <Stack
                  direction={
                    isMobile
                      ? "column"
                      : "row"
                  }
                  justifyContent="space-between"
                  alignItems={
                    isMobile
                      ? "stretch"
                      : "center"
                  }
                  spacing={1}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Search using Student IDs, filters,
                    or both.
                  </Typography>
              
                  <Stack
                    direction={
                      isMobile
                        ? "column"
                        : "row"
                    }
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      onClick={
                        resetFilters
                      }
                      disabled={
                        loadingStudents
                      }
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 800,
                      }}
                    >
                      Clear
                    </Button>
                  
                    <Button
                      variant="contained"
                      startIcon={
                        loadingStudents ? (
                          <CircularProgress
                            size={16}
                            sx={{
                              color: "#fff",
                            }}
                          />
                        ) : (
                          <SearchIcon />
                        )
                      }
                      onClick={
                        loadStudents
                      }
                      disabled={
                        loadingStudents
                      }
                      sx={{
                        minWidth: 170,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 900,
                        bgcolor: ORANGE,
                      
                        "&:hover": {
                          bgcolor: ORANGE_2,
                        },
                      }}
                    >
                      {loadingStudents
                        ? "Loading Students..."
                        : "Load Students"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            {/* Student ID search */}
            <Paper
              sx={{
                ...sectionSx,
                p: {
                  xs: 2,
                  md: 2.5,
                },
              }}
            >
              <Box sx={{ pl: 1 }}>
                <Stack
                  direction={
                    isMobile
                      ? "column"
                      : "row"
                  }
                  justifyContent="space-between"
                  alignItems={
                    isMobile
                      ? "stretch"
                      : "flex-start"
                  }
                  spacing={2}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: ORANGE,
                        fontSize: "1.05rem",
                      }}
                    >
                      Search by Student ID
                    </Typography>
                  
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Enter multiple Student IDs separated
                      by commas, spaces, semicolons, or new
                      lines.
                    </Typography>
                  </Box>
                  
                  <Chip
                    size="small"
                    label={`${
                      parseStudentIds(
                        studentIdSearch
                      ).length
                    } ID(s) entered`}
                    color={
                      parseStudentIds(
                        studentIdSearch
                      ).length
                        ? "primary"
                        : "default"
                    }
                    variant="outlined"
                  />
                </Stack>
                
                <Stack
                  direction={
                    isMobile
                      ? "column"
                      : "row"
                  }
                  spacing={1.2}
                  alignItems="stretch"
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    label="Student IDs"
                    placeholder={
                      "22B21A0501, 22B21A0502\n22B21A0503"
                    }
                    value={studentIdSearch}
                    onChange={(event) => {
                      setStudentIdSearch(
                        event.target.value
                      );
                    }}
                    onKeyDown={(event) => {
                      /*
                       * Ctrl + Enter on Windows/Linux.
                       * Cmd + Enter on macOS.
                       */
                      if (
                        event.key === "Enter" &&
                        (
                          event.ctrlKey ||
                          event.metaKey
                        )
                      ) {
                        event.preventDefault();
                    
                        if (
                          parseStudentIds(
                            studentIdSearch
                          ).length
                        ) {
                          loadStudents();
                        }
                      }
                    }}
                    sx={inputSx}
                  />

                  <Button
                    variant="contained"
                    startIcon={
                      loadingStudents ? (
                        <CircularProgress
                          size={16}
                          sx={{
                            color: "#fff",
                          }}
                        />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={loadStudents}
                    disabled={
                      loadingStudents ||
                      !parseStudentIds(
                        studentIdSearch
                      ).length
                    }
                    sx={{
                      minWidth: {
                        xs: "100%",
                        sm: 160,
                      },
                  
                      minHeight: {
                        xs: 44,
                        sm: "auto",
                      },
                  
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: ORANGE,
                  
                      "&:hover": {
                        bgcolor: ORANGE_2,
                      },
                  
                      "&.Mui-disabled": {
                        bgcolor:
                          "rgba(240,120,40,0.35)",
                        color:
                          "rgba(255,255,255,0.8)",
                      },
                    }}
                  >
                    {loadingStudents
                      ? "Searching..."
                      : "Search IDs"}
                  </Button>
                    
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStudentIdSearch("");
                      setStudents([]);
                      setSelectedStudentIds([]);
                      setPreviewReport(null);
                      setError("");
                    }}
                    disabled={
                      loadingStudents ||
                      !studentIdSearch.trim()
                    }
                    sx={{
                      minWidth: {
                        xs: "100%",
                        sm: 90,
                      },
                  
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
                
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 1,
                  }}
                >
                  Press Ctrl + Enter or Command + Enter to
                  search from the keyboard.
                </Typography>
              </Box>
            </Paper>
          </Stack>          

          {loadingStudents && (
            <LinearProgress
              sx={{ mt: 1.5 }}
            />
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 1.5,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {students.length > 0 && (
            <Paper
              sx={{
                ...cardSx,
                mt: 2,
              }}
            >

<Box
  sx={{
    p: 1.5,
    bgcolor: "#ffffff",
    borderBottom:
      "1px solid rgba(25,118,210,0.12)",

    position: "sticky",
    top: 0,
    zIndex: 2,
  }}
>
  <Stack
    direction={
      isMobile
        ? "column"
        : "row"
    }
    justifyContent="space-between"
    alignItems={
      isMobile
        ? "stretch"
        : "center"
    }
    spacing={1.5}
  >
    <Box>
      <Typography
        sx={{
          fontWeight: 900,
          color: "#172554",
        }}
      >
        Matching Students
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
      >
        Select students to download individual
        progress report PDFs.
      </Typography>
    </Box>

    <Stack
      direction={
        isMobile
          ? "column"
          : "row"
      }
      alignItems={
        isMobile
          ? "stretch"
          : "center"
      }
      spacing={1}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={
              allSelected
            }
            indeterminate={
              selectedStudentIds.length >
                0 &&
              !allSelected
            }
            onChange={
              toggleAll
            }
          />
        }
        label={`Select all (${students.length})`}
      />

      <Chip
        label={`${selectedStudentIds.length} selected`}
        color={
          selectedStudentIds.length
            ? "primary"
            : "default"
        }
        variant="outlined"
      />

      <Button
        variant="contained"
        startIcon={
          pdfLoading ? (
            <CircularProgress
              size={16}
              sx={{
                color: "#fff",
              }}
            />
          ) : (
            <DownloadIcon />
          )
        }
        onClick={
          downloadSelectedPdf
        }
        disabled={
          !selectedStudentIds.length ||
          pdfLoading
        }
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 900,
          bgcolor: ORANGE,

          "&:hover": {
            bgcolor: ORANGE_2,
          },
        }}
      >
        {pdfLoading
          ? "Generating PDFs..."
          : "Download Selected PDFs"}
      </Button>
    </Stack>
  </Stack>
</Box>
             {/*   
              <Stack
                direction={
                  isMobile
                    ? "column"
                    : "row"
                }
                justifyContent="space-between"
                alignItems={
                  isMobile
                    ? "stretch"
                    : "center"
                }
                spacing={1}
                sx={{ p: 1.5 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        selectedStudentIds.length >
                          0 && !allSelected
                      }
                      onChange={toggleAll}
                    />
                  }
                  label={`Select all (${students.length})`}
                />

                <Stack
                  direction={
                    isMobile
                      ? "column"
                      : "row"
                  }
                  spacing={1}
                >
                  <Chip
                    label={`${selectedStudentIds.length} student(s) selected`}
                    color={
                      selectedStudentIds.length
                        ? "primary"
                        : "default"
                    }
                  />

                  <Button
                    variant="contained"
                    startIcon={
                      pdfLoading ? (
                        <CircularProgress
                          size={16}
                          sx={{
                            color: "#fff",
                          }}
                        />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={
                      downloadSelectedPdf
                    }
                    disabled={
                      !selectedStudentIds.length ||
                      pdfLoading
                    }
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: ORANGE,

                      "&:hover": {
                        bgcolor: ORANGE_2,
                      },
                    }}
                  >
                    {pdfLoading
                      ? "Generating PDFs..."
                      : "Download Selected PDFs"}
                  </Button>
                </Stack>
              </Stack>
              */}

              <TableContainer
                sx={{ maxHeight: 520 }}
              >
                <Table
                  size="small"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={headCellSx}
                      />

                      <TableCell
                        sx={headCellSx}
                      >
                        Student ID
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Full Name
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Academic Year
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Sub Campus
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Degree
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Branch
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Semester
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Section
                      </TableCell>

                      <TableCell
                        sx={headCellSx}
                      >
                        Preview
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {students.map(
                      (
                        student,
                        index
                      ) => {
                        const key =
                          getStudentKey(
                            student
                          );

                        const checked =
                          selectedStudentIds.includes(
                            key
                          );

                        return (
                          <TableRow
                            key={key}
                            hover
                            sx={{
                              bgcolor:
                                index %
                                  2 ===
                                0
                                  ? "rgba(25,118,210,0.05)"
                                  : "#fff",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  checked
                                }
                                onChange={() =>
                                  toggleStudent(
                                    student
                                  )
                                }
                              />
                            </TableCell>

                            <TableCell>
                              {
                                student.StudentID
                              }
                            </TableCell>

                            <TableCell>
                              {
                                student.FullName
                              }
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.AcademicYear
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.SubCampusCode
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.DegreeCode
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.BranchCode
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.SemesterCode
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                student.Section
                              )}
                            </TableCell>

                            <TableCell>
                              <Button
                                size="small"
                                onClick={() =>
                                  previewStudent(
                                    student
                                  )
                                }
                                disabled={
                                  loadingReport
                                }
                                sx={{
                                  textTransform:
                                    "none",
                                  fontWeight: 800,
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>

      {/*
        This is rendered outside the visible screen,
        but it must not use display:none because
        html2canvas cannot measure display:none content.
      */}
      <Box
        ref={pdfStageRef}
        aria-hidden="true"
        sx={{
          position: "fixed",
          left: "-20000px",
          top: 0,
          width: 1120,
          bgcolor: "#fff",
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        {pdfReports.map((report) => (
          <ReportContent
            key={
              report?.student
                ?.StudentKey ||
              report?.student?.StudentID
            }
            report={report}
          />
        ))}
      </Box>
    </Box>
  );
}